import React, { useEffect, useMemo, useState } from "react";
import AuditHaccpHistory from "./AuditHaccpHistory";

import TopBar from "../../components/TopBar";
import DocumentHeader from "../../components/DocumentHeader";
import DocumentFooter from "../../components/DocumentFooter";
import { useBranding } from "../../hooks/useBranding";

const API = "/api";

function todayISO() {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
}

function buildInitialItems(template) {
    const out = [];
    for (const ph of template?.phases || []) {
        for (const it of ph.items || []) {
            out.push({
                fase_key: ph.key,
                fase_label: ph.label,
                voce_key: it.key,
                voce_label: it.label,
                esito: "C",
                anomalia: "",
                provvedimenti: "",
            });
        }
    }
    return out;
}

function groupByPhase(items) {
    const map = new Map();
    for (const r of items) {
        if (!map.has(r.fase_key)) map.set(r.fase_key, { label: r.fase_label, rows: [] });
        map.get(r.fase_key).rows.push(r);
    }
    return Array.from(map.entries()).map(([key, v]) => ({ key, label: v.label, rows: v.rows }));
}

export default function AuditHaccpPage() {
    const { branding, brandingLoading } = useBranding();

    const [historyKey, setHistoryKey] = useState(0);
    const [loading, setLoading] = useState(true);
    const [tpl, setTpl] = useState(null);
    const [dataVerifica, setDataVerifica] = useState(todayISO());
    const [verificatore, setVerificatore] = useState("");
    const [noteFinali, setNoteFinali] = useState("");
    const [rows, setRows] = useState([]);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);

    useEffect(() => {
        (async () => {
            setLoading(true);
            setMsg(null);
            try {
                const r = await fetch(`${API}/audit_haccp_template.php`, { credentials: "include" });
                const j = await r.json();
                if (!j.success) throw new Error(j.error || "Errore template");
                setTpl(j.template);
                setRows(buildInitialItems(j.template));
            } catch (e) {
                setMsg({ type: "error", text: e.message || "Errore" });
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const phases = useMemo(() => groupByPhase(rows), [rows]);
    const ncCount = useMemo(() => rows.filter((r) => r.esito === "NC").length, [rows]);

    function updateRow(fase_key, voce_key, patch) {
        setRows((prev) =>
            prev.map((r) => (r.fase_key === fase_key && r.voce_key === voce_key ? { ...r, ...patch } : r))
        );
    }

    function validateBeforeSave() {
        if (!dataVerifica) return "Seleziona la data verifica.";
        if (!verificatore.trim()) return "Inserisci il nome del verificatore.";

        for (const r of rows) {
            if (r.esito === "NC") {
                if (!String(r.anomalia || "").trim()) {
                    return `Manca "Anomalie" in: ${r.fase_label} → ${r.voce_label}`;
                }
                if (!String(r.provvedimenti || "").trim()) {
                    return `Manca "Provvedimenti" in: ${r.fase_label} → ${r.voce_label}`;
                }
            }
        }
        return null;
    }

    async function onSave() {
        const err = validateBeforeSave();
        if (err) {
            setMsg({ type: "error", text: err });
            return;
        }

        setSaving(true);
        setMsg(null);

        try {
            const r = await fetch(`${API}/audit_haccp_save.php`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    data_verifica: dataVerifica,
                    verificatore: verificatore.trim(),
                    note_finali: noteFinali,
                    items: rows,
                }),
            });

            const j = await r.json();
            if (!j.success) throw new Error(j.error || "Errore salvataggio");

            setMsg({ type: "ok", text: `Salvato. ID audit: ${j.audit_id}` });
            setHistoryKey((k) => k + 1);
        } catch (e) {
            setMsg({ type: "error", text: e.message || "Errore" });
        } finally {
            setSaving(false);
        }
    }

    if (loading) {
        return (
            <>
                <TopBar />
                <div className="p-4">Caricamento...</div>
            </>
        );
    }

    return (
        <>
            <TopBar />

            <div className="p-4 max-w-6xl mx-auto">
                {!brandingLoading && (
                    <DocumentHeader
                        branding={branding}
                        docTitle="Audit HACCP — Allegato 14"
                        docSubtitle="Verifica applicazione procedure HACCP"
                    />
                )}

                <div className="mb-4">
                    <h1 className="text-2xl font-semibold">Audit HACCP — Verifica applicazione procedure</h1>
                    <p className="text-sm opacity-80">
                        Mensilmente viene verificata per ogni fase la corretta applicazione delle procedure
                        descritte nel piano di autocontrollo.
                    </p>
                </div>

                <AuditHaccpHistory refreshKey={historyKey} />

                {msg && (
                    <div className={`mb-4 rounded-xl p-3 ${msg.type === "ok" ? "bg-green-100" : "bg-red-100"}`}>
                        <div className="text-sm">{msg.text}</div>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
                    <div className="rounded-2xl shadow p-3 bg-white">
                        <label className="text-sm font-medium">Data verifica</label>
                        <input
                            type="date"
                            className="mt-1 w-full border rounded-xl p-2"
                            value={dataVerifica}
                            onChange={(e) => setDataVerifica(e.target.value)}
                        />
                    </div>

                    <div className="rounded-2xl shadow p-3 bg-white md:col-span-2">
                        <label className="text-sm font-medium">Firma / Verificatore</label>
                        <input
                            className="mt-1 w-full border rounded-xl p-2"
                            placeholder="Nome e Cognome"
                            value={verificatore}
                            onChange={(e) => setVerificatore(e.target.value)}
                        />
                    </div>

                    <div className="rounded-2xl shadow p-3 bg-white">
                        <label className="text-sm font-medium">Non conformità rilevate</label>
                        <div className="mt-2 text-3xl font-semibold">{ncCount}</div>
                    </div>
                </div>

                <div className="space-y-5">
                    {phases.map((ph) => (
                        <div key={ph.key} className="rounded-2xl shadow bg-white overflow-hidden">
                            <div className="px-4 py-3 border-b">
                                <h2 className="font-semibold">{ph.label}</h2>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="min-w-[1100px] w-full text-sm">
                                    <thead>
                                        <tr className="bg-gray-50 text-left">
                                            <th className="p-3 w-[34%]">Voce</th>
                                            <th className="p-3 w-[10%] text-center">Conforme</th>
                                            <th className="p-3 w-[12%] text-center">Non conforme</th>
                                            <th className="p-3 w-[22%]">Anomalie *</th>
                                            <th className="p-3 w-[22%]">Provvedimenti</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {ph.rows.map((r) => {
                                            const isNC = r.esito === "NC";

                                            return (
                                                <tr key={r.voce_key} className={`border-t ${isNC ? "bg-red-50" : ""}`}>
                                                    <td className="p-3 align-top">{r.voce_label}</td>

                                                    <td className="p-3 align-top text-center">
                                                        <label className="inline-flex items-center gap-2 cursor-pointer">
                                                            <input
                                                                type="radio"
                                                                name={`${r.fase_key}-${r.voce_key}`}
                                                                checked={r.esito === "C"}
                                                                onChange={() =>
                                                                    updateRow(r.fase_key, r.voce_key, {
                                                                        esito: "C",
                                                                        anomalia: "",
                                                                        provvedimenti: "",
                                                                    })
                                                                }
                                                            />
                                                            <span>Conforme</span>
                                                        </label>
                                                    </td>

                                                    <td className="p-3 align-top text-center">
                                                        <label className="inline-flex items-center gap-2 cursor-pointer">
                                                            <input
                                                                type="radio"
                                                                name={`${r.fase_key}-${r.voce_key}`}
                                                                checked={r.esito === "NC"}
                                                                onChange={() =>
                                                                    updateRow(r.fase_key, r.voce_key, { esito: "NC" })
                                                                }
                                                            />
                                                            <span>Non conforme</span>
                                                        </label>
                                                    </td>

                                                    <td className="p-3 align-top">
                                                        <textarea
                                                            className="w-full border rounded-xl p-2 min-h-[44px]"
                                                            placeholder={isNC ? "Obbligatorio se non conforme" : "—"}
                                                            value={r.anomalia}
                                                            onChange={(e) =>
                                                                updateRow(r.fase_key, r.voce_key, {
                                                                    anomalia: e.target.value,
                                                                })
                                                            }
                                                            disabled={!isNC}
                                                        />
                                                    </td>

                                                    <td className="p-3 align-top">
                                                        <textarea
                                                            className="w-full border rounded-xl p-2 min-h-[44px]"
                                                            placeholder={isNC ? "Obbligatorio se non conforme" : "—"}
                                                            value={r.provvedimenti}
                                                            onChange={(e) =>
                                                                updateRow(r.fase_key, r.voce_key, {
                                                                    provvedimenti: e.target.value,
                                                                })
                                                            }
                                                            disabled={!isNC}
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <div className="px-4 py-3 border-t text-xs opacity-80">
                                *In caso di anomalia rilevata in fase di controllo, annotarla e prendere gli opportuni
                                provvedimenti.
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-6 rounded-2xl shadow p-4 bg-white">
                    <label className="text-sm font-medium">Note finali (opzionale)</label>
                    <textarea
                        className="mt-1 w-full border rounded-xl p-2 min-h-[90px]"
                        value={noteFinali}
                        onChange={(e) => setNoteFinali(e.target.value)}
                        placeholder="Note o osservazioni finali..."
                    />

                    <div className="mt-4 flex gap-3">
                        <button
                            onClick={onSave}
                            disabled={saving}
                            className="px-4 py-2 rounded-xl bg-black text-white disabled:opacity-60"
                        >
                            {saving ? "Salvataggio..." : "Salva Audit"}
                        </button>

                        <button
                            type="button"
                            onClick={() => window.print()}
                            className="px-4 py-2 rounded-xl border"
                        >
                            Stampa
                        </button>
                    </div>
                </div>

                <DocumentFooter />
            </div>
        </>
    );
}